import React, { PropsWithChildren } from 'react';

import cx from 'classnames';
import { useSelector } from 'react-redux';

import { FormBuilder } from 'components/Layouts/Form/FormBuilder';
import { AILoaderWrapper } from 'components/Pages/Admin/Page/common/AIPageGeneration/AILoaderWrapper';
import {
  PageGenerationProvider,
  usePageGenerationContext,
} from 'components/Pages/Admin/Page/common/AIPageGeneration/PageGenerationContext';
import Builder from 'components/Pages/Admin/Page/Landing/Builder';
import { useInlineEditor } from 'components/Pages/Admin/Page/Landing/functions';
import { getPageTypesConfig } from 'components/Pages/Admin/Page/Landing/pageTypesConfig';
import ErrorScreen from 'components/shared/ErrorScreen';
import Loader from 'components/shared/Loader';
import { usePageHook } from 'hooks/pages/usePageHook';
import { pageDataSelector } from 'store/page/selectors';

import { IContentPage } from '../types';
import s from './index.module.css';

type PageLandingContentProps = {
  withAIActions?: boolean;
};

type StateHandlerProps = {
  page: IContentPage;
};

function withStateHandler(WrappedComponent: React.ComponentType) {
  return function ({ page }: StateHandlerProps) {
    const { loading, error } = usePageHook(page);

    if (loading) {
      return (
        <div className={s.loaderContainer}>
          <Loader />
        </div>
      );
    }

    if (error) {
      return <ErrorScreen status="404" />;
    }

    return <WrappedComponent />;
  };
}

function withPageGenerationProvider(WrappedComponent: React.ComponentType<PageLandingContentProps>) {
  return function () {
    return (
      <PageGenerationProvider initialShowAIActions={true}>
        <WrappedComponent withAIActions={true} />
      </PageGenerationProvider>
    );
  };
}

const BuilderWithAILoader = ({ children }: PropsWithChildren<{}>) => {
  const { cardState } = usePageGenerationContext();

  return <AILoaderWrapper isLoading={cardState === 'loading'}>{children}</AILoaderWrapper>;
};

function PageLandingContent({ withAIActions }: PageLandingContentProps) {
  const page = useSelector(pageDataSelector) as IContentPage;
  const { useMockData } = getPageTypesConfig(page.type);
  const { landingData } = useInlineEditor(useMockData(page));
  const headlineHint = 'This text can only be updated in the previous step.';

  const renderBuilder = React.useMemo(() => {
    if (withAIActions)
      return (
        <BuilderWithAILoader>
          <Builder landingData={landingData.pages[0]} page={page} hideBurgerMenu headlineHint={headlineHint} />
        </BuilderWithAILoader>
      );

    return <Builder landingData={landingData.pages[0]} page={page} hideBurgerMenu headlineHint={headlineHint} />;
  }, [withAIActions, page, landingData.pages[0]]);

  if (!landingData) return null;

  return (
    <div className={cx(s.landingContainer, withAIActions && s.withoutTopPadding)}>
      {/* @ts-ignore */}
      <FormBuilder
        className={s.formBuilder}
        actionsClassName={withAIActions && s.actionsClassName}
        noTopPadding
        withoutCustomCSS
      >
        {renderBuilder}
      </FormBuilder>
    </div>
  );
}

export const PageLanding = withStateHandler(PageLandingContent);
export const PageLandingWithAI = withStateHandler(withPageGenerationProvider(PageLandingContent));
